import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { catalogApi } from '../api/catalog';
import { contentApi } from '../api/content';
import type { CategoryDto } from '../api/types';
import { ProductCard, ProductGridSkeleton } from '../components/ProductCard';
import { Seo } from '../components/Seo';
import { ErrorState, Skeleton } from '../components/ui';
import { formatDate, money } from '../lib/format';

/* ------------------------------------------------------------------ *
 *  Modern, sade, düzenli ve dinamik vitrin ana sayfası.
 * ------------------------------------------------------------------ */

export function HomePage() {
  return (
    <div className="space-y-16 pb-20 bg-white">
      <Seo
        title="Kutsal Toprakların Bereketli Lezzetleri & Hac Malzemeleri"
        description="Taze Medine mebrum ve acve hurmaları, lüks mihraplı kadife seccadeler, hakiki kuka tesbihler ve eksiksiz hac & umre malzemeleri en uygun fiyatlarla Hurmahane'de."
        type="website"
      />

      {/* 1. Tam Arka Plan Görselli Hero Banner Slider */}
      <HeroSlider />

      {/* 2. Avantajlar & Güven Rozetleri */}
      <TrustBar />

      {/* 3. Öne Çıkan Kategoriler (Yalnızca öne çıkarılan varsa gösterilir) */}
      <CategoriesShowcase />

      {/* 4. Öne Çıkan Ürünler */}
      <FeaturedProductsSection />

      {/* 5. Çiftli Kampanya Promosyon Bannerları (DB'de varsa gösterilir) */}
      <DualPromoBanners />

      {/* 6. Öne Çıkan Koleksiyonlar (Yalnızca öne çıkarılan varsa gösterilir) */}
      <CollectionsSection />

      {/* 7. Öne Çıkan Markalar (Yalnızca öne çıkarılan varsa gösterilir) */}
      <BrandsSection />

      {/* 8. Alışveriş Rehberleri & Blog (DB'de varsa gösterilir) */}
      <BlogSection />

      {/* 9. VIP Kulüp & E-Bülten Kaydı */}
      <NewsletterBanner />
    </div>
  );
}

/* ================================================================== *
 *  1. HERO BANNER SLIDER (TAM ARKA PLAN GÖRSELİ + ÜSTÜNDE YAZILAR/BUTONLAR)
 * ================================================================== */

interface BannerExtraMeta {
  b?: string;
  b2?: string;
  f1?: string;
  f2?: string;
}

function getHeroBannerMeta(
  altText: string | null | undefined,
  title: string | undefined,
  defaultShippingText: string,
): Required<BannerExtraMeta> {
  if (altText && altText.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(altText) as BannerExtraMeta;
      return {
        b: parsed.b?.trim() ?? '',
        b2: parsed.b2?.trim() ?? '',
        f1: parsed.f1?.trim() ?? '',
        f2: parsed.f2?.trim() ?? '',
      };
    } catch {
      // JSON parse hatasında varsayılana dön
    }
  }
  return {
    b: altText && altText !== title ? altText : 'ÖZEL SEZON KAMPANYASI',
    b2: 'Tüm Ürünleri İncele',
    f1: 'Kutsal Topraklardan %100 Orijinal',
    f2: defaultShippingText,
  };
}

function HeroSlider() {
  const { data: remoteBanners, isLoading } = useQuery({
    queryKey: ['content', 'banners', 'home-hero'],
    queryFn: () => contentApi.banners('home-hero'),
    staleTime: 30_000,
  });

  const { data: settings } = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 5 * 60_000,
  });

  const freeShippingThreshold = Number(
    settings?.['checkout.free_shipping_threshold'] ||
    settings?.['store.freeShippingThreshold'] ||
    750,
  );

  const slides = remoteBanners ?? [];
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [isPaused, slides.length]);

  if (isLoading) {
    return (
      <section className="relative overflow-hidden bg-ink-900 py-16 lg:py-24">
        <div className="container-page">
          <div className="max-w-2xl space-y-4">
            <Skeleton className="h-6 w-40 rounded-full bg-white/15" />
            <Skeleton className="h-12 w-3/4 rounded-xl bg-white/15" />
            <Skeleton className="h-5 w-2/3 rounded-lg bg-white/15" />
            <div className="pt-4 flex gap-3">
              <Skeleton className="h-12 w-44 rounded-xl bg-white/15" />
              <Skeleton className="h-12 w-36 rounded-xl bg-white/15" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (slides.length === 0) {
    return null;
  }

  const slide = slides[current] ?? slides[0];
  const defaultShippingText =
    freeShippingThreshold > 0
      ? `${money(freeShippingThreshold)} Üzeri Ücretsiz Kargo`
      : 'Hızlı & Güvenilir Kargo';
  const meta = getHeroBannerMeta(slide?.altText, slide?.title, defaultShippingText);

  return (
    <section
      className="relative overflow-hidden bg-ink-950 text-white"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Tam Arka Plan Görseli */}
      {slide?.imageUrl ? (
        <img
          key={slide.id}
          src={slide.imageUrl}
          alt={slide?.title ?? 'Banner'}
          className="absolute inset-0 h-full w-full object-cover object-center transition duration-1000 scale-100"
        />
      ) : null}

      {/* Yazıların Net Okunması İçin Koyu Gradyan Katman */}
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950/90 via-ink-950/70 to-ink-950/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 via-transparent to-ink-950/20" />

      {/* İçerik Alanı (Görselin Üzerinde) */}
      <div className="container-page relative z-10 flex min-h-[460px] flex-col justify-center py-14 sm:min-h-[520px] lg:min-h-[560px] lg:py-20">
        <div className="max-w-2xl">
          {meta.b ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide text-amber-300 backdrop-blur-md">
              <span className="h-2 w-2 animate-pulse rounded-full bg-amber-400" />
              <span>{meta.b}</span>
            </div>
          ) : null}

          <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-white drop-shadow-sm sm:text-5xl lg:text-6xl leading-[1.12]">
            {slide?.title}
          </h1>

          {slide?.subtitle ? (
            <p className="mt-4 max-w-xl text-base text-white/85 sm:text-lg leading-relaxed drop-shadow-xs">
              {slide.subtitle}
            </p>
          ) : null}

          <div className="mt-8 flex flex-wrap items-center gap-3.5">
            <Link
              to={slide?.linkUrl ?? '/urunler'}
              className="btn-primary rounded-xl px-7 py-3.5 text-base font-semibold shadow-lg shadow-brand-600/30 transition hover:scale-[1.02] hover:shadow-xl"
              onClick={() => {
                if (slide?.id) void contentApi.trackBannerClick(slide.id);
              }}
            >
              <span>{slide?.buttonText || 'Alışverişe Başla'}</span>
              <span>→</span>
            </Link>

            {meta.b2 ? (
              <Link
                to="/urunler"
                className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
              >
                {meta.b2}
              </Link>
            ) : null}
          </div>

          {/* Hızlı Güven İpuçları */}
          {meta.f1 || meta.f2 ? (
            <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-white/15 pt-6 text-xs text-white/80">
              {meta.f1 ? (
                <span className="flex items-center gap-1.5 font-medium">
                  <svg className="h-4 w-4 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {meta.f1}
                </span>
              ) : null}
              {meta.f2 ? (
                <span className="flex items-center gap-1.5 font-medium">
                  <svg className="h-4 w-4 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {meta.f2}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Slider Kontrolleri */}
        {slides.length > 1 ? (
          <div className="mt-10 flex items-center justify-between border-t border-white/15 pt-5">
            <div className="flex items-center gap-2">
              {slides.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => setCurrent(idx)}
                  className={`h-2 transition-all cursor-pointer ${
                    idx === current ? 'w-8 rounded-full bg-amber-400' : 'w-2 rounded-full bg-white/35 hover:bg-white/60'
                  }`}
                  aria-label={`Slayt ${idx + 1}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrent((prev) => (prev - 1 + slides.length) % slides.length)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white backdrop-blur-md transition hover:bg-white/25 cursor-pointer"
                aria-label="Önceki Slayt"
              >
                ←
              </button>
              <button
                onClick={() => setCurrent((prev) => (prev + 1) % slides.length)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white backdrop-blur-md transition hover:bg-white/25 cursor-pointer"
                aria-label="Sonraki Slayt"
              >
                →
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  2. AVANTAJLAR VE GUVEN ROZETLERI (TRUST BAR)
 * ================================================================== */

function TrustBar() {
  const perks = [
    {
      icon: (
        <svg viewBox="0 0 24 24" className="h-6 w-6 text-brand-600" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="1" y="3" width="15" height="13" rx="1" />
          <path d="M16 8h4l3 3v5h-7V8zM5.5 18a2.5 2.5 0 1 0 0 .01M18.5 18a2.5 2.5 0 1 0 0 .01" />
        </svg>
      ),
      title: 'Hızlı & Güvenli Kargo',
      desc: 'Özenle paketlenmiş taze ve güvenli gönderim',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" className="h-6 w-6 text-brand-600" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      ),
      title: '%100 Taze & Orijinal Mahsul',
      desc: 'Doğrudan Medine-i Münevvere bahçelerinden ithal',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      ),
      title: 'Toptan & Perakende Satış',
      desc: 'Hac ve cemiyet ikramları için özel fiyat avantajları',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      title: 'Güvenli Alışveriş & İade',
      desc: 'Koşulsuz iade ve müşteri memnuniyeti garantisi',
    },
  ];

  return (
    <section className="container-page pt-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {perks.map((perk, index) => (
          <div
            key={index}
            className="flex items-start gap-4 rounded-2xl border border-ink-200 bg-white p-5 shadow-sm transition hover:border-brand-300 hover:shadow-card"
          >
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-ink-50">
              {perk.icon}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-ink-900">{perk.title}</h3>
              <p className="mt-1 text-xs text-ink-500 leading-relaxed">{perk.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  3. ÖNE ÇIKAN KATEGORİLER (YALNIZCA ÖNE ÇIKARILAN VARSA GÖSTERİLİR)
 * ================================================================== */

function flattenCategories(nodes: CategoryDto[]): CategoryDto[] {
  return nodes.flatMap((node) => [node, ...flattenCategories(node.children ?? [])]);
}

function CategoriesShowcase() {
  const { data: categories } = useQuery({
    queryKey: ['catalog', 'category-tree'],
    queryFn: () => catalogApi.categoryTree(),
    staleTime: 60_000,
  });

  const featuredCategories = flattenCategories(categories ?? []).filter(
    (c) => c.isFeatured && c.isActive !== false,
  );

  if (featuredCategories.length === 0) return null;

  return (
    <section className="container-page">
      <SectionHeading
        title="Öne Çıkan Kategoriler"
        subtitle="İhtiyacınız olan ürünleri kategorilere göre kolayca keşfedin"
        href="/urunler"
        linkText="Tüm Ürünleri Gör"
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
        {featuredCategories.map((category) => (
          <Link
            key={category.id}
            to={`/kategori/${category.slug}`}
            className="group flex flex-col items-center overflow-hidden rounded-2xl border border-ink-200 bg-white p-4 text-center transition hover:-translate-y-1 hover:border-brand-400 hover:shadow-lg"
          >
            <div className="relative mb-3 flex aspect-square w-full max-w-[120px] items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-brand-50 to-ink-100">
              {category.imageUrl ? (
                <img
                  src={category.imageUrl}
                  alt={category.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                />
              ) : (
                <span className="text-3xl font-bold text-brand-600">{category.name[0]}</span>
              )}
            </div>
            <span className="text-sm font-semibold text-ink-800 transition group-hover:text-brand-700">
              {category.name}
            </span>
            <span className="mt-1 rounded-full bg-ink-100 px-2.5 py-0.5 text-[11px] font-medium text-ink-600">
              {category.productCount} ürün
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  4. ONE CIKAN URUNLER (SADE & ZARIF VITRIN)
 * ================================================================== */

function FeaturedProductsSection() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['products', 'featured-home'],
    queryFn: () => catalogApi.searchProducts({ pageSize: 8, featuredOnly: true }),
  });

  const products = data?.items ?? [];

  return (
    <section className="container-page">
      <SectionHeading
        title="Öne Çıkan Ürünler"
        subtitle="En çok tercih edilen ve sizin için seçtiğimiz popüler ürünler"
        href="/urunler"
        linkText="Tüm Ürünleri İncele"
      />

      {isLoading ? (
        <ProductGridSkeleton />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : products.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-brand-200 bg-gradient-to-b from-brand-50/40 to-white py-16 px-6 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-2xl shadow-inner">
            🌴
          </div>
          <h3 className="mt-4 text-lg font-bold text-brand-950">Henüz Öne Çıkan Ürün Bulunmuyor</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-ink-600">
            Mağazamızdaki tüm ürünleri incelemek için ürün kataloğumuza göz atabilirsiniz.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/urunler" className="btn-primary btn-sm rounded-lg">
              Tüm Ürünleri Gör
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

/* ================================================================== *
 *  5. CIFTLI KAMPANYA BANNERLARI (VERİTABANINDAN YÜKLENİR)
 * ================================================================== */

function DualPromoBanners() {
  const { data: promoBanners } = useQuery({
    queryKey: ['content', 'banners', 'home-promo'],
    queryFn: () => contentApi.banners('home-promo'),
    staleTime: 30_000,
  });

  const items = promoBanners ?? [];
  if (items.length === 0) return null;

  return (
    <section className="container-page">
      <div className={`grid grid-cols-1 gap-6 ${items.length === 1 ? 'md:grid-cols-1 max-w-2xl mx-auto' : items.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {items.map((b, idx) => {
          const isEven = idx % 2 === 0;
          const gradient = isEven
            ? 'border-brand-200/80 bg-gradient-to-br from-brand-50/40 via-white to-ink-50 hover:border-brand-300'
            : 'border-rose-200/80 bg-gradient-to-br from-rose-50/40 via-white to-amber-50/30 hover:border-rose-300';
          const badgeClass = isEven ? 'bg-brand-100 border-brand-200 text-brand-800' : 'bg-rose-100 border-rose-200 text-rose-800';
          const btnClass = isEven ? 'bg-brand-600 hover:bg-brand-700' : 'bg-rose-600 hover:bg-rose-700';
          let badgeText = isEven ? 'ÖZEL FIRSAT' : 'KAMPANYA';
          if (b.altText && b.altText.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(b.altText) as BannerExtraMeta;
              badgeText = parsed.b?.trim() ?? '';
            } catch {
              // ignore
            }
          } else if (b.altText && b.altText !== b.title) {
            badgeText = b.altText;
          }
          const isExternal = b.linkUrl ? /^https?:\/\//i.test(b.linkUrl) : false;

          return (
            <div
              key={b.id}
              className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border p-6 sm:p-8 shadow-sm transition hover:shadow-md sm:flex-row sm:items-center ${gradient}`}
            >
              <div className="relative z-10 max-w-xs">
                {badgeText ? (
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${badgeClass}`}>
                    {badgeText}
                  </span>
                ) : null}
                <h3 className={`${badgeText ? 'mt-3' : ''} text-xl font-bold text-ink-900 sm:text-2xl`}>{b.title}</h3>
                {b.subtitle ? <p className="mt-1 text-xs text-ink-600 sm:text-sm">{b.subtitle}</p> : null}
                {isExternal ? (
                  <a
                    href={b.linkUrl!}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={`mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm transition ${btnClass}`}
                  >
                    <span>{b.buttonText || 'Keşfet'}</span>
                    <span>→</span>
                  </a>
                ) : (
                  <Link
                    to={b.linkUrl ?? '/urunler'}
                    className={`mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm transition ${btnClass}`}
                  >
                    <span>{b.buttonText || 'Keşfet'}</span>
                    <span>→</span>
                  </Link>
                )}
              </div>
              <div className="mt-4 sm:mt-0 flex justify-center sm:justify-end">
                {b.imageUrl ? (
                  <img
                    src={b.imageUrl}
                    alt={b.title}
                    className="h-36 w-36 sm:h-44 sm:w-44 rounded-2xl object-cover shadow-md transition duration-500 group-hover:scale-105"
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  6. ÖZEL KOLEKSİYONLAR (YALNIZCA ÖNE ÇIKARILAN VARSA GÖSTERİLİR)
 * ================================================================== */

function CollectionsSection() {
  const { data: collections } = useQuery({
    queryKey: ['collections', 'home'],
    queryFn: () => catalogApi.searchCollections({ pageSize: 20 }),
    staleTime: 60_000,
  });

  const featuredCollections = (collections?.items ?? []).filter(
    (c) => c.isFeatured && c.isActive !== false,
  );
  if (featuredCollections.length === 0) return null;

  return (
    <section className="container-page">
      <SectionHeading
        title="Özel Koleksiyonlar"
        subtitle="Sizin için özenle hazırladığımız seçkin ürün koleksiyonları"
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {featuredCollections.map((collection) => (
          <Link
            key={collection.id}
            to={`/koleksiyon/${collection.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm transition hover:-translate-y-1 hover:border-brand-300 hover:shadow-card"
          >
            <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-brand-50 to-ink-100">
              {collection.imageUrl ? (
                <img
                  src={collection.imageUrl}
                  alt={collection.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-3xl font-bold text-brand-600">
                  {collection.title[0]}
                </div>
              )}
            </div>
            <div className="p-4">
              <h3 className="text-base font-bold text-ink-900 transition group-hover:text-brand-700">
                {collection.title}
              </h3>
              <p className="mt-1 text-xs text-ink-500">{collection.productCount} ürün mevcut</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  7. ÖNE ÇIKAN MARKALAR (YALNIZCA ÖNE ÇIKARILAN VARSA GÖSTERİLİR)
 * ================================================================== */

function BrandsSection() {
  const { data: brandsData } = useQuery({
    queryKey: ['catalog', 'brands'],
    queryFn: () => catalogApi.searchBrands({ pageSize: 30 }),
    staleTime: 60_000,
  });

  const featuredBrands = (brandsData?.items ?? []).filter(
    (b) => b.isFeatured && b.isActive !== false,
  );
  if (featuredBrands.length === 0) return null;

  return (
    <section className="container-page">
      <SectionHeading
        title="Öne Çıkan Markalar"
        subtitle="Güvenilir üreticilerin orijinal ve seçkin ürünleri"
        href="/urunler"
        linkText="Tüm Ürünleri Gör"
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
        {featuredBrands.map((brand) => (
          <Link
            key={brand.id}
            to={`/marka/${brand.slug}`}
            className="group flex flex-col items-center justify-center rounded-2xl border border-ink-200 bg-white p-5 text-center transition hover:-translate-y-1 hover:border-brand-400 hover:shadow-card"
          >
            <div className="flex h-16 w-28 items-center justify-center overflow-hidden">
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={brand.name}
                  loading="lazy"
                  className="max-h-14 max-w-full object-contain transition duration-300 group-hover:scale-105"
                />
              ) : (
                <span className="text-lg font-bold text-brand-700">{brand.name}</span>
              )}
            </div>
            <span className="mt-2.5 text-xs font-semibold text-ink-700 transition group-hover:text-brand-700">
              {brand.name}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  9. ALISVERIS REHBERLERI & BLOG
 * ================================================================== */

function BlogSection() {
  const { data: blogData } = useQuery({
    queryKey: ['content', 'blog', 'home'],
    queryFn: () => contentApi.blogPosts({ pageSize: 3 }),
  });

  const posts = blogData?.items ?? [];
  if (posts.length === 0) return null;

  return (
    <section className="container-page">
      <SectionHeading
        title="Alışveriş Rehberleri & Blog"
        subtitle="Trendler, ürün incelemeleri ve yaşam tarzı ipuçları"
        href="/blog"
        linkText="Tüm Yazıları Oku"
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {posts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group flex flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-card"
          >
            <div className="relative aspect-[16/9] overflow-hidden bg-ink-100">
              {post.coverImageUrl ? (
                <img
                  src={post.coverImageUrl}
                  alt={post.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              ) : null}
              {post.categoryName ? (
                <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-semibold text-ink-800 shadow-sm backdrop-blur">
                  {post.categoryName}
                </span>
              ) : null}
            </div>

            <div className="flex flex-1 flex-col p-5">
              <div className="flex items-center gap-3 text-xs text-ink-400">
                <span>{formatDate(post.publishedAt)}</span>
                <span>•</span>
                <span>{post.readingTimeMinutes} dk okuma</span>
              </div>

              <h3 className="mt-2 line-clamp-2 text-base font-bold text-ink-900 transition group-hover:text-brand-700">
                {post.title}
              </h3>

              {post.summary ? (
                <p className="mt-2 line-clamp-2 text-xs text-ink-500 leading-relaxed">{post.summary}</p>
              ) : null}

              <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 group-hover:underline">
                Devamını Oku →
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ================================================================== *
 *  12. VIP KULUP & BULTEN KAYDI (BEYAZ VE AYDINLIK TASARIM)
 * ================================================================== */

function NewsletterBanner() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done'>('idle');
  const [msg, setMsg] = useState('');

  const { data: settings } = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 10 * 60_000,
  });

  const discountAmount = settings?.['store.newsletter_discount_amount'] || '100';
  const couponCode = settings?.['store.newsletter_coupon_code'] || 'BULTEN100';
  const minSubtotal = settings?.['store.newsletter_min_subtotal'];

  const handleSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('sending');
    try {
      await contentApi.subscribeNewsletter({ email, source: 'home-vip' });
      setStatus('done');
      setMsg(`Harika! ${discountAmount} TL indirim kuponunuz (${couponCode}) e-posta adresinize gönderildi.`);
      setEmail('');
    } catch {
      setStatus('done');
      setMsg('Bülten kaydınız başarıyla alındı.');
      setEmail('');
    }
  };

  return (
    <section className="container-page">
      <div className="rounded-3xl border border-brand-200 bg-gradient-to-b from-brand-50/50 to-white p-8 text-center text-ink-900 sm:p-12 shadow-sm">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 border border-brand-200 px-3.5 py-1 text-xs font-bold text-brand-800">
          🎁 VIP AYRICALIKLAR KULÜBÜ
        </span>

        <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-ink-900 sm:text-4xl">
          İlk Siparişine Özel {discountAmount} TL Kupon Kazan!
        </h2>

        <p className="mx-auto mt-3 max-w-xl text-sm text-ink-600 sm:text-base">
          E-bültenimize katılın{minSubtotal && Number(minSubtotal) > 0 ? `, ${minSubtotal} TL üzeri alışverişlerinizde geçerli` : ''}, gizli indirim kodları, yeni sezon lansmanları ve özel kampanya bildirimlerini ilk siz alın.
        </p>

        {status === 'done' ? (
          <div className="mx-auto mt-6 max-w-md rounded-xl bg-brand-50 border border-brand-300 p-4 text-brand-800 text-sm font-semibold">
            ✓ {msg}
          </div>
        ) : (
          <form onSubmit={handleSubscribe} className="mx-auto mt-8 flex max-w-md flex-col gap-3 sm:flex-row">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-posta adresinizi girin..."
              className="flex-1 rounded-xl border border-ink-300 bg-white px-4 py-3 text-sm text-ink-900 placeholder-ink-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 shadow-sm"
            />
            <button
              type="submit"
              disabled={status === 'sending'}
              className="btn-primary rounded-xl px-6 py-3 font-semibold shadow-sm hover:shadow"
            >
              {status === 'sending' ? 'Kaydediliyor...' : 'Kuponu Al'}
            </button>
          </form>
        )}

        <p className="mt-4 text-[11px] text-ink-500">
          İstediğiniz zaman tek tıkla abonelikten ayrılabilirsiniz. Verileriniz KVKK kapsamında korunur.
        </p>
      </div>
    </section>
  );
}

/* ================================================================== *
 *  YARDIMCI BILESENLER
 * ================================================================== */

function SectionHeading({
  title,
  subtitle,
  href,
  linkText,
}: {
  title: string;
  subtitle?: string;
  href?: string;
  linkText?: string;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-ink-900 sm:text-2xl">{title}</h2>
        {subtitle ? <p className="mt-1 text-xs sm:text-sm text-ink-500">{subtitle}</p> : null}
      </div>
      {href && linkText ? (
        <Link to={href} className="text-xs sm:text-sm font-semibold text-brand-700 transition hover:text-brand-900 hover:underline">
          {linkText} →
        </Link>
      ) : null}
    </div>
  );
}
